/**
 * Picture-in-Picture anchor: renders a tiny "Recording…" canvas, captures it
 * as a video stream, and requests PiP. While PiP is active, the browser
 * treats the tab as foregrounded, so MediaRecorder keeps capturing even if
 * the user switches apps. Chrome/Android supported; iOS Safari does not
 * support stream-capture PiP.
 */

type PipCanvas = {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  video: HTMLVideoElement
  stream: MediaStream
  frameHandle: number | null
  startedAt: number
}

let state: PipCanvas | null = null
let exitListenerCleanup: (() => void) | null = null

type WindowWithPip = Window & { documentPictureInPicture?: unknown }
type DocumentWithPipEnabled = Document & { pictureInPictureEnabled?: boolean }
type HTMLVideoElementWithPip = HTMLVideoElement & {
  requestPictureInPicture?: () => Promise<unknown>
}

export function isPipSupported(): boolean {
  if (typeof document === 'undefined') return false
  const doc = document as DocumentWithPipEnabled
  if (!doc.pictureInPictureEnabled) return false
  const probe = document.createElement('video') as HTMLVideoElementWithPip
  return typeof probe.requestPictureInPicture === 'function'
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function draw(p: PipCanvas): void {
  const { ctx, canvas } = p
  const w = canvas.width
  const h = canvas.height
  const elapsed = Date.now() - p.startedAt

  ctx.fillStyle = '#0b1426'
  ctx.fillRect(0, 0, w, h)

  const dotRadius = 14
  const dotX = 40
  const dotY = h / 2
  const pulse = 0.6 + 0.4 * Math.abs(Math.sin(elapsed / 500))
  ctx.fillStyle = `rgba(239, 68, 68, ${pulse.toFixed(2)})`
  ctx.beginPath()
  ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#ffffff'
  ctx.font = '600 28px system-ui, sans-serif'
  ctx.textBaseline = 'middle'
  ctx.fillText('REC', 70, dotY - 12)
  ctx.font = '400 22px system-ui, sans-serif'
  ctx.fillStyle = '#22d3ee'
  ctx.fillText(formatElapsed(elapsed), 70, dotY + 18)
}

function loop(): void {
  if (!state) return
  draw(state)
  state.frameHandle = window.requestAnimationFrame(loop)
}

export async function enterPipAnchor(onExit?: () => void): Promise<void> {
  if (!isPipSupported()) {
    throw new Error('Picture-in-Picture is not supported on this browser.')
  }
  if (state) return

  const canvas = document.createElement('canvas')
  canvas.width = 320
  canvas.height = 180
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable.')

  const stream = canvas.captureStream(30)

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.srcObject = stream
  video.style.position = 'fixed'
  video.style.width = '1px'
  video.style.height = '1px'
  video.style.opacity = '0'
  video.style.pointerEvents = 'none'
  document.body.appendChild(video)

  try {
    await video.play()
  } catch (err) {
    video.remove()
    stream.getTracks().forEach((t) => t.stop())
    throw err
  }

  state = {
    canvas,
    ctx,
    video,
    stream,
    frameHandle: null,
    startedAt: Date.now(),
  }
  loop()

  const handleLeave = () => {
    void exitPipAnchor()
    onExit?.()
  }
  video.addEventListener('leavepictureinpicture', handleLeave)
  exitListenerCleanup = () => {
    video.removeEventListener('leavepictureinpicture', handleLeave)
  }

  try {
    const v = video as HTMLVideoElementWithPip
    if (!v.requestPictureInPicture) throw new Error('requestPictureInPicture missing')
    await v.requestPictureInPicture()
  } catch (err) {
    await exitPipAnchor()
    throw err
  }
}

export async function exitPipAnchor(): Promise<void> {
  const current = state
  state = null
  exitListenerCleanup?.()
  exitListenerCleanup = null

  if (!current) return

  if (current.frameHandle !== null) {
    window.cancelAnimationFrame(current.frameHandle)
  }

  try {
    if (document.pictureInPictureElement === current.video) {
      await document.exitPictureInPicture()
    }
  } catch {
    // ignore — may already have exited
  }

  current.stream.getTracks().forEach((t) => t.stop())
  current.video.srcObject = null
  current.video.remove()
}

export function isPipAnchorActive(): boolean {
  return state !== null
}

// Keep the module import tree-shakable if documentPictureInPicture is ever
// consulted elsewhere.
export function hasDocumentPipApi(): boolean {
  return (
    typeof window !== 'undefined' &&
    'documentPictureInPicture' in (window as WindowWithPip)
  )
}
